"use client";

import {
  type TranslationNamespaceKeys,
  useTranslations,
} from "@frontend/lib/next-intl-typed";
import { cn } from "@frontend/shared/lib/utils";
import { format } from "date-fns";
import { RotateCcw, Signature } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { Control, FieldError } from "react-hook-form";
import { Controller } from "react-hook-form";
import SignatureCanvas from "react-signature-canvas";
import { KioskButton } from "./KioskButton";
import {
  KioskDialog,
  KioskDialogContent,
  KioskDialogFooter,
  KioskDialogHeader,
  KioskDialogTitle,
} from "./KioskDialog";

/**
 * Signature data structure
 * Matches the schema from IntakeForm.tsx
 */
export interface SignatureData {
  signature:
    | { dataUrl: string }
    | { text: string; style?: SignatureStyle }
    | string; // Nested signature object or legacy string
  date: string;
  isSignatureComplete: boolean;
}

/**
 * Content for signature modal translations
 */
export interface SignatureModalContent {
  tapToSign: string;
  noSignature: string;
  signYourName: string;
  typeYourName: string;
  clear: string;
  typeNameInstead: string;
  fullName: string;
  enterFullName: string;
  preview: string;
  chooseStyle: string;
  print: string;
  classic: string;
  fancy: string;
  drawSignatureInstead: string;
  cancel: string;
  sign: string;
  signedOn: string;
  edit: string;
  yourSignature: string;
  usePreviousSignature: string;
  pleaseSignYourNameWithYourFinger: string;
  consumerFullName: string;
  signed: string;
}

/**
 * KioskSignatureInput Props
 */
export interface KioskSignatureInputProps {
  // biome-ignore lint/suspicious/noExplicitAny: Control type is generic
  control: Control<any>;
  name: string;
  /** Question label text */
  questionText: string;
  /** HTML blurb/instructions to display above the signature area */
  blurb?: string;
  required?: boolean;
  error?: FieldError;
  disabled?: boolean;
  className?: string;
  // Optional: Previous signature for reuse (future enhancement)
  previousSignature?: SignatureData | null;
  // Optional: Require new signature (ignore previous)
  requireNewSignature?: boolean;
  /** Translated content for the signature modal */
  content?: SignatureModalContent;
  /** Optional: Callback to clear field errors */
  clearErrors?: (name?: string | string[]) => void;
}

enum SignatureStyle {
  Print = "print",
  Classic = "classic",
  Fancy = "fancy",
}

/**
 * KioskSignatureInput Component
 *
 * Dual-mode signature input for kiosk intake forms.
 * Supports both canvas drawing and typed signatures.
 *
 * Features:
 * - Canvas drawing with react-signature-canvas
 * - Typed signature with style selection (print, classic, fancy)
 * - Modal dialog for signature capture
 * - Signature preview after signing
 * - Clear/edit functionality
 * - Automatic timestamp
 * - Validation support
 */
export const KioskSignatureInput = ({
  control,
  name,
  questionText,
  blurb,
  required = false,
  error,
  disabled = false,
  className,
  previousSignature,
  requireNewSignature = false,
  content,
  clearErrors,
}: KioskSignatureInputProps) => {
  // Default content (English fallback)
  const defaultContent: SignatureModalContent = {
    tapToSign: "Tap to sign",
    noSignature: "No signature",
    signYourName: "Sign your name",
    typeYourName: "Type your name",
    clear: "Clear",
    typeNameInstead: "Type your name instead",
    fullName: "Full Name",
    enterFullName: "Enter your full name",
    preview: "Preview",
    chooseStyle: "Choose style",
    print: "Print",
    classic: "Classic",
    fancy: "Fancy",
    drawSignatureInstead: "Draw signature instead",
    cancel: "Cancel",
    sign: "Sign",
    signedOn: "Signed on:",
    edit: "Edit",
    yourSignature: "Your signature",
    usePreviousSignature: "Use previous signature",
    pleaseSignYourNameWithYourFinger: "Please sign your name with your finger",
    consumerFullName: "",
    signed: "Signed,",
  };

  const t_intake = useTranslations("IntakeFormStates");

  const t = content || defaultContent;

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [signMode, setSignMode] = useState<"drawn" | "typed">("drawn");
  const [signatureStyle, setSignatureStyle] = useState<SignatureStyle>(
    SignatureStyle.Classic,
  );
  const [typedSignature, setTypedSignature] = useState(
    content?.consumerFullName || "",
  );
  const sigCanvas = useRef<SignatureCanvas>(null);

  // Check if there's a previous signature available
  const hasPreviousSignature =
    !requireNewSignature &&
    previousSignature &&
    previousSignature.isSignatureComplete &&
    previousSignature.signature;

  const [signatureFilled, setSignatureFilled] = useState(
    !!hasPreviousSignature,
  );

  const sanitizeHtml = (html: string) => {
    if (html.includes("<script>")) {
      throw new Error("XSS attack detected");
    }
    return html;
  };

  const fontClassName = useMemo(() => {
    switch (signatureStyle) {
      case "print":
        return "font-vujahday";
      case "classic":
        return "font-madi";
      case "fancy":
        return "font-delafield";
      default:
        return "font-madi";
    }
  }, [signatureStyle]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const currentSignature = field.value as SignatureData | undefined;
        const hasSigned = currentSignature?.isSignatureComplete;

        const handleSave = () => {
          const timestamp = format(new Date(), "MM/dd/yyyy hh:mm a");

          if (signMode === "drawn" && !sigCanvas.current?.isEmpty()) {
            const dataUrl = sigCanvas.current?.toDataURL();
            field.onChange({
              signature: { dataUrl },
              date: timestamp,
              isSignatureComplete: true,
            });
          } else if (signMode === "typed" && typedSignature.trim()) {
            field.onChange({
              signature: {
                text: typedSignature,
                style: signatureStyle,
              },
              date: timestamp,
              isSignatureComplete: true,
            });
          }

          // Clear errors for this field
          clearErrors?.(name);

          setIsDialogOpen(false);
          setSignatureFilled(false);
        };

        const handleClear = () => {
          field.onChange({
            signature: "",
            date: "",
            isSignatureComplete: false,
          });
        };

        const handleCancel = () => {
          setIsDialogOpen(false);
          sigCanvas.current?.clear();
          setSignatureFilled(false);
          setSignMode("drawn");
        };

        const handleEdit = () => {
          // Pre-populate with existing signature
          if (currentSignature?.signature) {
            setSignatureFilled(true);
            if (typeof currentSignature.signature === "object") {
              if ("dataUrl" in currentSignature.signature) {
                setSignMode("drawn");
                // Canvas will be populated in useEffect
              } else if ("text" in currentSignature.signature) {
                setSignMode("typed");
                setTypedSignature(currentSignature.signature.text);
                setSignatureStyle(
                  currentSignature.signature.style || SignatureStyle.Classic,
                );
              }
            }
          }
          setIsDialogOpen(true);
        };

        const handleUsePrevious = () => {
          if (hasPreviousSignature && previousSignature) {
            field.onChange(previousSignature);
            // Clear errors for this field
            clearErrors?.(name);
          }
        };

        // Load existing signature when dialog opens
        // biome-ignore lint/correctness/useExhaustiveDependencies: Loading signature data when dialog opens
        useEffect(() => {
          if (isDialogOpen) {
            // First, try to load current signature
            const signatureToLoad =
              currentSignature?.signature ||
              (hasPreviousSignature && previousSignature?.signature
                ? previousSignature.signature
                : null);

            if (signatureToLoad) {
              if (typeof signatureToLoad === "object") {
                if ("dataUrl" in signatureToLoad) {
                  if (signMode === "typed") {
                    setSignMode("drawn");
                  }
                  setSignatureFilled(true);
                  setTimeout(() => {
                    sigCanvas.current?.fromDataURL(signatureToLoad.dataUrl);
                  }, 100);
                } else if ("text" in signatureToLoad) {
                  if (signMode === "drawn") {
                    setSignMode("typed");
                  }
                  setTypedSignature(signatureToLoad.text);
                  setSignatureStyle(
                    signatureToLoad.style || SignatureStyle.Classic,
                  );
                  setSignatureFilled(true);
                }
              }
            }
          }
        }, [isDialogOpen]);

        // Reload drawn signature when switching to drawn mode
        // biome-ignore lint/correctness/useExhaustiveDependencies: Reloading canvas when mode changes
        useEffect(() => {
          if (isDialogOpen && signMode === "drawn") {
            const signatureToLoad =
              currentSignature?.signature || previousSignature?.signature;
            if (
              signatureToLoad &&
              typeof signatureToLoad === "object" &&
              "dataUrl" in signatureToLoad
            ) {
              setSignatureFilled(true);
              setTimeout(() => {
                sigCanvas.current?.fromDataURL(signatureToLoad.dataUrl);
              }, 100);
            } else {
              setSignatureFilled(false);
            }
          }
        }, [signMode, isDialogOpen]);

        const RenderedSignature = () => {
          if (!currentSignature?.signature) return null;

          if (typeof currentSignature.signature === "object") {
            if ("dataUrl" in currentSignature.signature) {
              return (
                <img
                  src={currentSignature.signature.dataUrl}
                  alt={t.yourSignature}
                  className="max-h-[90px] max-w-[85%] object-contain"
                />
              );
            }
            if ("text" in currentSignature.signature) {
              const fontClass =
                currentSignature.signature.style === "print"
                  ? "font-vujahday"
                  : currentSignature.signature.style === "classic"
                    ? "font-madi"
                    : "font-delafield";
              return (
                <p className={cn("text-5xl", fontClass)}>
                  {currentSignature.signature.text}
                </p>
              );
            }
          }

          // Legacy string signature
          return (
            <p className="text-xl font-serif italic">
              {String(currentSignature.signature)}
            </p>
          );
        };

        return (
          <div className={cn("flex flex-col gap-6 w-full", className)}>
            {/* Unified card: blurb + signature trigger */}
            <div
              className={cn(
                "flex flex-col rounded-3xl overflow-hidden max-h-[calc(100dvh-320px)]",
                error && "border border-red-500/50",
              )}
            >
              {/* Scrollable blurb area */}
              {blurb && (
                <div
                  className="flex-1 min-h-0 bg-white px-8 py-6 overflow-y-auto
                  [&::-webkit-scrollbar]:w-[10px]
                  [&::-webkit-scrollbar-track]:bg-transparent
                  [&::-webkit-scrollbar-track]:rounded-full
                  [&::-webkit-scrollbar-track]:my-2
                  [&::-webkit-scrollbar-thumb]:bg-gray-300
                  [&::-webkit-scrollbar-thumb]:rounded-full
                  [&::-webkit-scrollbar-thumb]:hover:bg-gray-400
                  [&::-webkit-scrollbar-thumb]:border-[3px]
                  [&::-webkit-scrollbar-thumb]:border-solid
                  [&::-webkit-scrollbar-thumb]:border-transparent
                  [&::-webkit-scrollbar-thumb]:bg-clip-padding"
                >
                  <div
                    className="text-gray-700 prose prose-sm max-w-none"
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized HTML from CMS
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(blurb),
                    }}
                  />
                </div>
              )}

              {/* Signature trigger / signed display */}
              <div className="w-full">
                <KioskDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                  <button
                    type="button"
                    onClick={() => {
                      if (disabled) return;
                      if (hasSigned) {
                        handleEdit();
                        return;
                      }
                      if (
                        !hasSigned &&
                        hasPreviousSignature &&
                        previousSignature
                      ) {
                        handleUsePrevious();
                        return;
                      }
                      setIsDialogOpen(true);
                    }}
                    disabled={disabled}
                    className={cn(
                      "relative flex items-center justify-center gap-2 shrink-0",
                      "h-[101px] w-full p-4",
                      "transition-all duration-200",
                      disabled
                        ? "cursor-not-allowed opacity-60"
                        : "active:scale-[0.99]",
                      !hasSigned && "bg-white/50",
                    )}
                  >
                    {hasSigned && (
                      <div
                        aria-hidden="true"
                        className="absolute inset-0 pointer-events-none rounded-b-3xl backdrop-blur-[10px] border border-white/20"
                        style={{
                          background: "rgba(255, 255, 255, 0.1)",
                          WebkitBackdropFilter: "blur(10px)",
                          boxShadow:
                            "0 4px 30px rgba(0, 0, 0, 0.06), inset 0 2px 20px rgba(0, 0, 0, 0.08), inset 0 0 10px rgba(255, 255, 255, 0.4)",
                        }}
                      />
                    )}
                    {hasSigned ? (
                      <div className="relative flex items-center gap-3">
                        <span className="text-2xl font-semibold tracking-tight">
                          {t.signed}
                        </span>
                        <div className="h-14 flex items-center">
                          <RenderedSignature />
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-semibold tracking-tight">
                          {t.tapToSign}
                        </span>
                        <Signature className="h-7 w-7" strokeWidth={1.5} />
                      </div>
                    )}
                  </button>

                  <KioskDialogContent className="w-[calc(100%-16px)] max-w-2xl min-h-[60vh] md:min-h-[720px] items-start flex flex-col justify-between">
                    <KioskDialogHeader>
                      <div className="flex items-start justify-start w-full gap-3">
                        <KioskDialogTitle className="text-2xl">
                          {signMode === "drawn"
                            ? t.signYourName
                            : t.typeYourName}
                        </KioskDialogTitle>

                        <Signature className="h-6 w-6" />
                      </div>
                    </KioskDialogHeader>

                    {/* Signature Mode Content */}
                    <div className="flex flex-col gap-4 w-full">
                      {signMode === "drawn" ? (
                        <>
                          {/* Canvas Area */}
                          <div
                            className="border-2 border-gray-100 rounded-2xl overflow-hidden bg-white"
                            data-signature-canvas
                          >
                            <SignatureCanvas
                              ref={sigCanvas}
                              canvasProps={{
                                className: "w-full h-[200px]",
                              }}
                              onEnd={() => setSignatureFilled(true)}
                            />
                          </div>

                          {/* Clear button */}
                          {signatureFilled && (
                            <div className="flex justify-start">
                              <KioskButton
                                type="button"
                                variant="outline"
                                size="sm"
                                className="flex items-center max-w-[100px] p-0 pr-2"
                                onClick={() => {
                                  sigCanvas.current?.clear();
                                  setSignatureFilled(false);
                                }}
                              >
                                <RotateCcw
                                  className="mr-2 inline h-4"
                                  color="black"
                                />
                                {t.clear}
                              </KioskButton>
                            </div>
                          )}

                          {/* Switch to typed mode */}
                          <KioskButton
                            size="lg"
                            type="button"
                            variant="outline"
                            className="py-4 text-black border leading-3 text-base font-medium"
                            onClick={() => {
                              setSignMode("typed");
                              setSignatureFilled(typedSignature.length > 0);
                            }}
                          >
                            {t.typeNameInstead}
                          </KioskButton>
                        </>
                      ) : (
                        <>
                          {/* Typed Signature Input */}
                          <div className="flex flex-col gap-2">
                            <label
                              htmlFor="typed-signature"
                              className="text-sm font-medium"
                            >
                              {t.fullName}
                            </label>
                            <input
                              id="typed-signature"
                              type="text"
                              className="w-full border-2 border-gray-300 rounded-xl p-4 font-medium focus:border-blue-500 focus:outline-none"
                              value={typedSignature}
                              onChange={(e) => {
                                setTypedSignature(e.target.value);
                                setSignatureFilled(e.target.value.length > 0);
                              }}
                              placeholder={t.enterFullName}
                              autoFocus
                            />
                          </div>

                          {/* Signature Preview */}
                          <div className="w-full min-h-[80px] bg-gray-50 border-2 border-gray-300 rounded-xl flex items-center justify-center p-4">
                            <p className={cn("text-7xl", fontClassName)}>
                              {typedSignature || t.preview}
                            </p>
                          </div>

                          {/* Style Selection */}
                          <div className="flex flex-col gap-2">
                            <p className="text-sm font-medium">
                              {t.chooseStyle}
                            </p>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  setSignatureStyle(SignatureStyle.Print)
                                }
                                className={cn(
                                  "flex-1 p-3 rounded-xl border-2 transition-all",
                                  signatureStyle === SignatureStyle.Print
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-gray-300 hover:border-gray-400",
                                )}
                              >
                                <span>{t.print}</span>
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setSignatureStyle(SignatureStyle.Classic)
                                }
                                className={cn(
                                  "flex-1 p-3 rounded-xl border-2 transition-all",
                                  signatureStyle === SignatureStyle.Classic
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-gray-300 hover:border-gray-400",
                                )}
                              >
                                <span>{t.classic}</span>
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setSignatureStyle(SignatureStyle.Fancy)
                                }
                                className={cn(
                                  "flex-1 p-3 rounded-xl border-2 transition-all",
                                  signatureStyle === SignatureStyle.Fancy
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-gray-300 hover:border-gray-400",
                                )}
                              >
                                <span>{t.fancy}</span>
                              </button>
                            </div>
                          </div>

                          {/* Switch to drawn mode */}
                          <KioskButton
                            size="lg"
                            type="button"
                            variant="outline"
                            className="py-4 text-black border leading-3 text-base font-medium flex"
                            onClick={() => {
                              setSignMode("drawn");
                              setSignatureFilled(!sigCanvas.current?.isEmpty());
                            }}
                          >
                            <Signature className="h-4 w-4 mr-2" />
                            {t.drawSignatureInstead}
                          </KioskButton>
                        </>
                      )}
                    </div>

                    <KioskDialogFooter className="items-end w-full">
                      <KioskButton
                        type="button"
                        variant="outline"
                        onClick={handleCancel}
                        className="flex-1"
                      >
                        {t.cancel}
                      </KioskButton>
                      <KioskButton
                        type="button"
                        onClick={handleSave}
                        disabled={!signatureFilled}
                        className="flex-1"
                      >
                        {t.sign}
                      </KioskButton>
                    </KioskDialogFooter>
                  </KioskDialogContent>
                </KioskDialog>
              </div>
            </div>

            {/* Error message */}
            {error && (
              <p className="text-sm text-red-600 px-2">
                {t_intake(
                  error.message as TranslationNamespaceKeys<"IntakeFormStates">,
                )}
              </p>
            )}
          </div>
        );
      }}
    />
  );
};

KioskSignatureInput.displayName = "KioskSignatureInput";
